/**
 * useInvestorPreview — Manages admin preview mode for the investor shell.
 *
 * When an admin enters preview mode, they can browse /investor/* routes
 * as if they were an investor. The preview flag is stored in sessionStorage
 * so it survives page navigation but clears on tab close.
 *
 * Integrates with investorAccessPolicy.isAdminPreviewMode().
 */

import { useCallback, useSyncExternalStore } from 'react';

const PREVIEW_KEY = 'ax1_investor_preview';
const PREVIEW_ORIGIN_KEY = 'ax1_investor_preview_origin';

// ── Tiny external store for cross-component reactivity ───────────────────────

const listeners = new Set<() => void>();

function subscribe(cb: () => void) {
    listeners.add(cb);
    return () => listeners.delete(cb);
}

function getSnapshot(): boolean {
    return sessionStorage.getItem(PREVIEW_KEY) === 'true';
}

function notify() {
    listeners.forEach((cb) => cb());
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useInvestorPreview() {
    const isPreviewMode = useSyncExternalStore(subscribe, getSnapshot, () => false);

    const enterPreview = useCallback((origin?: string) => {
        sessionStorage.setItem(PREVIEW_KEY, 'true');
        if (origin) sessionStorage.setItem(PREVIEW_ORIGIN_KEY, origin);
        notify();
    }, []);

    const exitPreview = useCallback(() => {
        const origin = sessionStorage.getItem(PREVIEW_ORIGIN_KEY);
        sessionStorage.removeItem(PREVIEW_KEY);
        sessionStorage.removeItem(PREVIEW_ORIGIN_KEY);
        notify();
        return origin;
    }, []);

    return { isPreviewMode, enterPreview, exitPreview } as const;
}
